import { useCallback, useEffect, useState } from 'react';
import { Canceler } from 'axios';
import isBoolean from 'lodash/isBoolean';
import { toast } from 'react-toastify';

import { ICreateAbsenceRequestDTO, IEmployeeBalanceDTO } from 'src/api/dtos';
import BalanceRepository from 'src/api/repositories/ess/v1/balance.repository';
import { useTranslate } from 'src/shared/providers';
import { mapValidateEmployeeBalanceRequest } from 'src/shared/utils';

let cancelBalanceValidation: Canceler | undefined;

interface IBalanceCheckProps {
  inputs: Partial<ICreateAbsenceRequestDTO>;
}

export const useBalanceCheck = ({ inputs }: IBalanceCheckProps) => {
  const [balance, setBalance] = useState<IEmployeeBalanceDTO | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslate();

  useEffect(() => {
    return () => {
      setBalance(undefined);
    };
  }, []);

  const checkBalance = useCallback(async () => {
    try {
      setLoading(true);
      const repository = new BalanceRepository();

      const params = mapValidateEmployeeBalanceRequest(inputs);
      const response = await repository.getFutureBalance(params, (c) => (cancelBalanceValidation = c));

      if (isBoolean(response.isScheduleConfident) && !response.isScheduleConfident) {
        toast.info(t('absences.insufficientSchedulingInformation'), { autoClose: 10000, type: toast.TYPE.WARNING });
      }

      setBalance(response);
    } catch (e: any) {
      if (e.status === 499) {
        return;
      }

      const message = e?.data?.errorMessage || t('global.oopsAnErrorOccurredWhileProcessingYourRequest');
      toast.info(message, { type: toast.TYPE.ERROR });
    } finally {
      setLoading(false);
    }
  }, [inputs]);

  const cancel = () => {
    if (!cancelBalanceValidation) {
      return;
    }

    cancelBalanceValidation();

    cancelBalanceValidation = undefined;
    setBalance(undefined);
  };

  return { balance, loading, checkBalance, cancel, reset: () => setBalance(undefined), setBalance };
};
